import React, { useState, useRef, useEffect } from 'react';
import { applyFilter } from './editorUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Editor.css';

const Editor = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [editedImage, setEditedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    grayscale: 0,
    sepia: 0,
    blur: 0,
    hueRotate: 0,
    invert: 0
  });
  
  const canvasRef = useRef(null);
  
  // Available filters and their configuration
  const filterControls = [
    { id: 'brightness', name: 'Brightness', min: 0, max: 200, step: 1, defaultValue: 100, unit: '%' },
    { id: 'contrast', name: 'Contrast', min: 0, max: 200, step: 1, defaultValue: 100, unit: '%' },
    { id: 'saturation', name: 'Saturation', min: 0, max: 200, step: 1, defaultValue: 100, unit: '%' },
    { id: 'grayscale', name: 'Grayscale', min: 0, max: 100, step: 1, defaultValue: 0, unit: '%' },
    { id: 'sepia', name: 'Sepia', min: 0, max: 100, step: 1, defaultValue: 0, unit: '%' },
    { id: 'blur', name: 'Blur', min: 0, max: 10, step: 0.1, defaultValue: 0, unit: 'px' },
    { id: 'hueRotate', name: 'Hue Rotate', min: 0, max: 360, step: 1, defaultValue: 0, unit: '°' },
    { id: 'invert', name: 'Invert', min: 0, max: 100, step: 1, defaultValue: 0, unit: '%' }
  ];

  // Apply filters when they change
  useEffect(() => {
    if (originalImage) {
      applyChanges();
    }
  }, [filters, originalImage]);

  const handleImageSelected = (img) => {
    setOriginalImage(img);
    
    // Reset filters
    resetFilters();
  };

  const handleFilterChange = (filterId, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterId]: value
    }));
  };

  const resetFilters = () => {
    const defaultFilters = {};
    filterControls.forEach(filter => {
      defaultFilters[filter.id] = filter.defaultValue;
    });
    setFilters(defaultFilters);
  };

  const applyChanges = async () => {
    if (!originalImage) return;
    
    setIsProcessing(true);
    try {
      const result = await applyFilter(originalImage, filters, canvasRef.current);
      setEditedImage(result);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!editedImage) return;
    
    const link = document.createElement('a');
    link.href = editedImage;
    link.download = 'edited-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate CSS filter string
  const getFilterStyle = () => {
    return {
      filter: `
        brightness(${filters.brightness}%)
        contrast(${filters.contrast}%)
        saturate(${filters.saturation}%)
        grayscale(${filters.grayscale}%)
        sepia(${filters.sepia}%)
        blur(${filters.blur}px)
        hue-rotate(${filters.hueRotate}deg)
        invert(${filters.invert}%)
      `
    };
  };

  return (
    <div className="editor tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Photo Editor</h1>
          <p>Adjust brightness, contrast, apply filters, and enhance your photos</p>
        </div>
        
        <div className="tool-content">
          {!originalImage && (
            <ImageUploader 
              onImageSelected={handleImageSelected}
              selectedImage={originalImage}
            />
          )}
          
          {originalImage && (
            <div className="editor-workspace">
              <div className="filter-controls">
                <h3>Adjustments</h3>
                <div className="filters-list">
                  {filterControls.map(filter => (
                    <div key={filter.id} className="filter-control">
                      <div className="filter-header">
                        <label htmlFor={filter.id}>{filter.name}</label>
                        <span>{filters[filter.id]}{filter.unit}</span>
                      </div>
                      <input 
                        type="range" 
                        id={filter.id} 
                        min={filter.min} 
                        max={filter.max} 
                        step={filter.step}
                        value={filters[filter.id]} 
                        onChange={(e) => handleFilterChange(filter.id, parseFloat(e.target.value))} 
                      />
                    </div>
                  ))}
                </div>
                
                <button 
                  className="btn btn-primary download-btn" 
                  onClick={handleDownload}
                  disabled={isProcessing || !editedImage}
                >
                  {isProcessing ? "Processing..." : "Download Edited Image"}
                </button>
                
                <button className="btn btn-secondary" onClick={resetFilters}>
                  Reset All Filters
                </button>
                
                <button 
                  className="btn btn-outline" 
                  onClick={() => setOriginalImage(null)}
                >
                  Upload New Image
                </button>
              </div>
              
              <div className="image-preview-area">
                <div className="image-preview-container">
                  <h3>Preview</h3>
                  <div className="image-preview">
                    <img 
                      src={originalImage.src} 
                      alt="Preview" 
                      style={getFilterStyle()} 
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Editor; 